'use client';

import Link from 'next/link';
import { useId, useState } from 'react';

import EnlaceCta from '@/components/marketing/comun/EnlaceCta';
import Resaltado from '@/components/marketing/comun/Resaltado';

import PestanasPoderesEscritorio from './PestanasPoderesEscritorio';
import PoderesMovil from './PoderesMovil';
import { FUNCION_INICIAL, type IdFuncionPoder } from './gruposPoderes';
import { TARJETAS_PODERES, TARJETAS_PODERES_MOVIL, TEXTOS_PODERES, type TarjetaPoderes } from './textosPoderes';

// Isla cliente (D-07): solo guarda qué función está elegida; el grupo del móvil, el panel y la
// demo derivan de ahí. Los dos árboles (Poderes_Chat y Poderes_Chat_Movil) se pintan siempre y se
// alternan con `hidden lg:block` / `lg:hidden`; ninguno tiene bucles ni observadores.

interface PropiedadesPoderesChat {
    /** La página de Poderes la pone a false: el enlace apuntaría a sí misma (UI-SPEC auto-answer 20). */
    mostrarEnlacePagina?: boolean;
    idSeccion?: string;
}

function Cabecera({ idTitulo }: { idTitulo: string }) {
    return (
        <div className="flex flex-col gap-[10px] lg:gap-3">
            <span className="text-[12px] font-bold uppercase tracking-[0.08em] text-primary lg:text-[13px]">
                {TEXTOS_PODERES.antetitulo}
            </span>
            <h2
                id={idTitulo}
                className="m-0 font-display text-[30px] font-bold leading-[1.12] tracking-[-0.02em] text-ink lg:text-[44px] lg:leading-[1.1]"
            >
                {TEXTOS_PODERES.titulo} <Resaltado>{TEXTOS_PODERES.tituloResaltado}</Resaltado>
            </h2>
            <p className="m-0 hidden max-w-[760px] text-[18px] leading-normal text-ink/90 lg:block">
                {TEXTOS_PODERES.entradilla}
            </p>
            <p className="m-0 text-[16px] leading-normal text-ink/90 lg:hidden">{TEXTOS_PODERES.entradillaMovil}</p>
        </div>
    );
}

const ESTILO_TARJETA = {
    escritorio: { caja: 'gap-[6px] rounded-[18px] px-[18px] py-4', titulo: 'text-[18px]' },
    movil: { caja: 'gap-1 rounded-ds-lg px-[14px] py-3', titulo: 'text-[16px]' },
} as const;

function Tarjetas({ tarjetas, variante }: { tarjetas: readonly TarjetaPoderes[]; variante: 'escritorio' | 'movil' }) {
    const estilo = ESTILO_TARJETA[variante];
    return (
        <div className={variante === 'escritorio' ? 'grid grid-cols-3 gap-5' : 'flex flex-col gap-[10px]'}>
            {tarjetas.map((t) => (
                <div
                    key={t.titulo}
                    data-parte="tarjeta"
                    className={`flex flex-col border-2 border-ink bg-surface-2 shadow-ds-2 ${estilo.caja}`}
                >
                    <strong className={`font-display ${estilo.titulo}`}>{t.titulo}</strong>
                    <span className="text-[14px] leading-normal text-ink/90">{t.texto}</span>
                </div>
            ))}
        </div>
    );
}

function FilaCta({ mostrarEnlacePagina }: { mostrarEnlacePagina: boolean }) {
    return (
        <div className="mt-[52px] flex items-center gap-4">
            <EnlaceCta href={TEXTOS_PODERES.cta.href} variante="primario" alto={52} flecha>
                {TEXTOS_PODERES.cta.texto}
            </EnlaceCta>
            {mostrarEnlacePagina && (
                <Link
                    href={TEXTOS_PODERES.enlacePagina.href}
                    className="text-[15px] font-semibold text-ink underline underline-offset-4 hover:decoration-2"
                >
                    {TEXTOS_PODERES.enlacePagina.texto}
                </Link>
            )}
        </div>
    );
}

export default function PoderesChat({ mostrarEnlacePagina = true, idSeccion }: PropiedadesPoderesChat) {
    const idBase = useId();
    const [funcion, setFuncion] = useState<IdFuncionPoder>(FUNCION_INICIAL);
    const idTitulo = `${idBase}-titulo`;
    return (
        <section id={idSeccion} aria-labelledby={idTitulo} className="scroll-mt-28 text-ink">
            <div className="mx-auto max-w-[1440px] px-4 pb-8 pt-10 lg:px-8 lg:pb-12 lg:pt-[72px] xl:px-20">
                <Cabecera idTitulo={idTitulo} />
                <div data-arbol="escritorio" className="mt-[76px] hidden lg:block">
                    <PestanasPoderesEscritorio idBase={idBase} seleccionada={funcion} alSeleccionar={setFuncion} />
                    <div className="mt-5">
                        <Tarjetas tarjetas={TARJETAS_PODERES} variante="escritorio" />
                    </div>
                    <FilaCta mostrarEnlacePagina={mostrarEnlacePagina} />
                </div>
                <div data-arbol="movil" className="mt-4 flex flex-col gap-4 lg:hidden">
                    <PoderesMovil idBase={`${idBase}-movil`} seleccionada={funcion} alSeleccionar={setFuncion} />
                    <Tarjetas tarjetas={TARJETAS_PODERES_MOVIL} variante="movil" />
                    <EnlaceCta href={TEXTOS_PODERES.cta.href} variante="primario" alto={52} flecha anchoCompleto>
                        {TEXTOS_PODERES.cta.texto}
                    </EnlaceCta>
                </div>
            </div>
        </section>
    );
}
